import { Injectable, OnDestroy } from '@angular/core';
import { DeviceFlagsService } from './device-flags.service';
import { AnimationTimingService } from './animation-timing.service';
import { Subject, Subscription } from 'rxjs';
import { AlarmFlags } from 'ops/common/models/alarm-flags';
import { ChannelFlags } from 'ops/common/models/device-flags';
import { Channel } from 'ops/common/models/channel';

@Injectable()
export class AlarmAnimationService implements OnDestroy {
    private CSS_VAR_UNACK = '--ops-alarm-color-unack';
    private CSS_VAR_ALARM_UNACK = '--ops-alarm-color-alarm-unack';
    private CSS_VAR_CANCEL_UNACK = '--ops-alarm-color-cancel-unack';
    private CSS_VAR_RESOLVED_UNACK = '--ops-alarm-resolved-unack';

    private classes: { [fcuChannelId: number]: object } = {};
    private alarmClasses: { [fcuChannelId: number]: object } = {};
    private flagSubscription: Subscription;
    private allFlagSubscription: Subscription;

    private unackColors: AlarmColorAlternator;
    private alarmUnackColors: AlarmColorAlternator;
    private cancelUnackColors: AlarmColorAlternator;
    private resolvedUnackColors: AlarmColorAlternator;

    private animationSubscription: Subscription;

    classUpdated$: Subject<void> = new Subject();

    constructor(
        private flagService: DeviceFlagsService,
        private animationTiming: AnimationTimingService,
    ) {
        this.unackColors = new AlarmColorAlternator(['#00000099', '#039b0599']);
        this.alarmUnackColors = new AlarmColorAlternator(['#00000099', '#e2000099']);
        this.cancelUnackColors = new AlarmColorAlternator(['#00000099', '#e2000099']);
        this.resolvedUnackColors = new AlarmColorAlternator(['#00000099', '#00800099']);

        this.flagSubscription = this.flagService.onFlagsUpdated().subscribe((v) => {
            this.writeFlags(v);
        });

        this.allFlagSubscription = this.flagService.onAllFlagsUpdated().subscribe(() => {
            this.alarmClassUpdated();
        });

        this.animationSubscription = this.animationTiming.onAlarmFlashTick().subscribe(() => {
            this.setGlobalCssColor(this.CSS_VAR_UNACK, this.unackColors.next());
            this.setGlobalCssColor(this.CSS_VAR_ALARM_UNACK, this.alarmUnackColors.next());
            this.setGlobalCssColor(this.CSS_VAR_CANCEL_UNACK, this.cancelUnackColors.next());
            this.setGlobalCssColor(this.CSS_VAR_RESOLVED_UNACK, this.resolvedUnackColors.next());
        });
    }

    ngOnDestroy(): void {
        this.flagSubscription?.unsubscribe();
        this.allFlagSubscription?.unsubscribe();
        this.animationSubscription?.unsubscribe();
    }

    private writeFlags(v: ChannelFlags) {
        this.classes[v.id] = this.calculateClasses(v);
        this.alarmClasses[v.id] = this.unackFlags(v);
    }

    getAlarmClasses(channel: Channel) {
        return this.classes[channel.fcuData.fcuChannelId] ?? {};
    }

    getByChannelId(channelId) {
        return this.classes[channelId] ?? {};
    }

    private calculateClasses(f: ChannelFlags) {
        return {
            'flag-alarm': this.aggregateFlags(f.flags.alarm) !== 0,
            'flag-cancel':
                (this.aggregateFlags(f.flags.cancelAuto1) |
                    this.aggregateFlags(f.flags.cancelAuto2) |
                    this.aggregateFlags(f.flags.cancelAuto3) |
                    this.aggregateFlags(f.flags.cancelManual)) !==
                0,
            'flag-unack': this.aggregateFlags(f.flags.unAck) !== 0,
        };
    }

    getClassByFlag(channelId: string, flag): 0 | 1 {
        if (this.alarmClasses[channelId]) {
            return this.alarmClasses[channelId][flag] ?? {};
        }
        return 0;
    }

    private unackFlags(f: ChannelFlags) {
        return f.flags.unAck;
    }

    private aggregateFlags(flags: AlarmFlags) {
        return (
            flags.under |
            flags.over |
            flags.feedbackError |
            flags.highHigh |
            flags.high |
            flags.low |
            flags.lowlow
        );
    }

    private setGlobalCssColor(key: string, color: string) {
        document.documentElement.style.setProperty(key, color);
    }

    private alarmClassUpdated() {
        this.classUpdated$.next();
    }
}

class AlarmColorAlternator {
    colors: string[];
    colorIndex = 0;

    constructor(colors: string[]) {
        this.colors = colors;
    }

    next(): string {
        return this.colors[++this.colorIndex % this.colors.length];
    }
}
